import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import type { ApiError, DonationCampaignRow, DonationRow, WallListingRow } from '../api';
import { donationEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars, humanize, toCents } from './event/shared';
import { ContentEditor } from '../content/ContentEditor';
import { useFeatures } from '../features';
import { PledgesPanel } from './Pledges';

/**
 * Donations (D-51): what people can give to, what they gave, and the totals.
 * Gifts never earn loyalty points; they do show on the customer's profile.
 */

const statusTone = (status: string): Tone => {
  switch (status) {
    case 'SUCCEEDED':
    case 'ACTIVE':
      return 'ok';
    case 'PENDING':
    case 'REFUNDED':
      return 'neutral';
    case 'PAST_DUE':
    case 'PAUSED':
      return 'warn';
    case 'FAILED':
    case 'CANCELED':
      return 'bad';
    default:
      return 'neutral';
  }
};

export default function DonationsPage() {
  const can = usePermissions();
  const features = useFeatures();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DonationCampaignRow | 'new' | null>(null);
  const [editingPage, setEditingPage] = useState<DonationCampaignRow | null>(null);
  const [moderating, setModerating] = useState<DonationCampaignRow | null>(null);
  // Search lands here with ?q= (D-340).
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<Record<'campaign_id' | 'status' | 'kind' | 'q', string | undefined>>({ campaign_id: undefined, status: undefined, kind: undefined, q: params.get('q') ?? undefined });
  const summary = useQuery({ queryKey: ['donations', 'summary'], queryFn: donationEndpoints.summary });
  const campaigns = useQuery({ queryKey: ['donations', 'campaigns'], queryFn: donationEndpoints.campaigns });
  const donations = useQuery({ queryKey: ['donations', 'list', filters], queryFn: () => donationEndpoints.donations(filters) });
  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['donations'] });
  };
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => donationEndpoints.cancelRecurring(id, reason),
    onSuccess: refresh,
    onError: (cause: ApiError) => setError(cause.message),
  });
  const uploadArtwork = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => donationEndpoints.uploadArtwork(id, file),
    onSuccess: refresh,
    onError: (cause: ApiError) => setError(cause.message),
  });
  const removeArtwork = useMutation({
    mutationFn: (id: string) => donationEndpoints.removeArtwork(id),
    onSuccess: refresh,
    onError: (cause: ApiError) => setError(cause.message),
  });
  const pickArtwork = (id: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) uploadArtwork.mutate({ id, file });
    };
    input.click();
  };

  if (!can('donations.view')) return <NotPermitted permission="donations.view" />;
  const canManage = can('donations.manage');
  const totals = summary.data?.totals;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="flex flex-wrap gap-4">
        <Stat label="Raised" value={dollars(totals?.raised_cents ?? 0)} detail={`${totals?.gifts ?? 0} gift${totals?.gifts === 1 ? '' : 's'} from ${totals?.donors ?? 0} donor${totals?.donors === 1 ? '' : 's'}`} />
        <Stat label="Recurring" value={String(totals?.recurring_active ?? 0)} detail={`${dollars(totals?.recurring_monthly_cents ?? 0)} per month`} />
        <Stat label="This month" value={dollars(summary.data?.by_month[0]?.amount_cents ?? 0)} detail={summary.data?.by_month[0] ? `${summary.data.by_month[0].gifts} gifts in ${summary.data.by_month[0].month}` : 'no gifts yet'} />
      </div>

      {editing ? <CampaignForm campaign={editing === 'new' ? null : editing} onDone={() => { setEditing(null); refresh(); }} onCancel={() => setEditing(null)} onError={(m) => setError(m)} /> : null}
      {moderating ? <WallModeration campaign={moderating} canManage={canManage} onClose={() => setModerating(null)} onError={(m) => setError(m)} /> : null}
      {editingPage ? <CampaignPageEditor campaign={editingPage} onDone={() => { setEditingPage(null); refresh(); }} onError={(m) => setError(m)} /> : null}
      <Panel
        title="Campaigns"
        description="What a donor can give to. One campaign may also be offered inside the ticket checkout."
        actions={canManage && !editing ? <Button variant="primary" onClick={() => setEditing('new')}>New campaign</Button> : null}
      >
        <Table headers={['Campaign', 'Artwork', 'Amounts', 'Options', 'Raised', 'Status', '']}>
          {(campaigns.data?.campaigns ?? []).length === 0 ? (
            <EmptyRow columns={7}>No campaigns yet. Create one and it appears at /donate on the storefront.</EmptyRow>
          ) : (
            campaigns.data!.campaigns.map((c) => (
              <tr key={c.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <div className="font-medium text-ink-900">{c.name}</div>
                  <div className="text-xs text-ink-400">/donate/{c.slug}</div>
                </td>
                <td className="px-5 py-3">
                  {c.artwork_file_id ? <img src={`${donationEndpoints.artworkUrl(c.id)}?v=${c.updated_at}`} alt="" className="aspect-[3/1] w-32 rounded border border-line object-cover" /> : <span className="text-xs text-ink-400">gradient</span>}
                  {canManage ? (
                    <div className="mt-1 flex gap-1">
                      <Button onClick={() => pickArtwork(c.id)} disabled={uploadArtwork.isPending}>{c.artwork_file_id ? 'Replace' : 'Upload'}</Button>
                      {c.artwork_file_id ? <Button variant="danger" onClick={() => removeArtwork.mutate(c.id)} disabled={removeArtwork.isPending}>Remove</Button> : null}
                    </div>
                  ) : null}
                </td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">
                  {c.suggested_amounts.map((a) => dollars(a)).join(' · ')}
                  <div className="text-xs text-ink-400">min {dollars(c.min_amount_cents)}{c.allow_custom_amount ? ' · custom ok' : ''}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {c.allow_recurring ? 'recurring' : 'one-time only'}
                  {c.offer_in_checkout ? <Badge tone="accent">at checkout</Badge> : null}
                  {c.default_for_refunds ? <Badge tone="accent">refund gifts</Badge> : null}
                  {c.tax_deductible ? <span className="ml-1 text-xs">tax-deductible</span> : null}
                  {/* Each fund posts to its own QuickBooks account, mapped like any other code (D-331). */}
                  {c.accounting_code ? (
                    <div className="mt-1 text-xs">
                      {features.quickbooks ? (
                        <Link to="/quickbooks" className="font-mono text-ink-400 hover:text-brand hover:underline" title="Map this fund to its QuickBooks account">
                          {c.accounting_code}
                        </Link>
                      ) : (
                        <span className="font-mono text-ink-400">{c.accounting_code}</span>
                      )}
                    </div>
                  ) : null}
                </td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">
                  {dollars(c.raised_cents ?? 0)}
                  <div className="text-xs text-ink-400">{c.gifts ?? 0} gifts · {c.recurring_active ?? 0} recurring</div>
                </td>
                <td className="px-5 py-3">
                  <Badge tone={c.active ? 'ok' : 'neutral'}>{c.active ? 'ACTIVE' : 'HIDDEN'}</Badge>
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {canManage ? (
                    <span className="inline-flex gap-2">
                      <Button onClick={() => setEditing(c)}>Edit</Button>
                      <Button onClick={() => { setEditing(null); setEditingPage(c); }}>Edit page</Button>
                      {c.show_donor_wall ? <Button onClick={() => setModerating(c)}>Donor wall</Button> : null}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {/* Pledged, paid and still to come (D-356). */}
      <PledgesPanel campaigns={campaigns.data?.campaigns ?? []} canManage={canManage} />

      <Panel
        title="Gifts"
        description="Every gift Stripe told us about. Recurring gifts list each renewal under their detail."
        actions={
          <div className="flex flex-wrap gap-2">
            <select className={INPUT} value={filters.campaign_id ?? ''} onChange={(e) => setFilters((f) => ({ ...f, campaign_id: e.target.value || undefined }))} aria-label="Campaign">
              <option value="">All campaigns</option>
              {(campaigns.data?.campaigns ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select className={INPUT} value={filters.kind ?? ''} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value || undefined }))} aria-label="Kind">
              <option value="">Any kind</option>
              <option value="ONE_TIME">One-time</option>
              <option value="RECURRING">Recurring</option>
              <option value="CHECKOUT">At checkout</option>
            </select>
            <select className={INPUT} value={filters.status ?? ''} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))} aria-label="Status">
              <option value="">Any status</option>
              {['PENDING', 'SUCCEEDED', 'ACTIVE', 'PAST_DUE', 'PAUSED', 'FAILED', 'CANCELED', 'ENDED', 'REFUNDED'].map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </select>
            <input className={INPUT} placeholder="Name or email" value={filters.q ?? ''} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value || undefined }))} aria-label="Search donors" />
          </div>
        }
      >
        <Table headers={['When', 'Donor', 'Campaign', 'Gift', 'Status', '']}>
          {(donations.data?.donations ?? []).length === 0 ? (
            <EmptyRow columns={6}>No gifts match.</EmptyRow>
          ) : (
            donations.data!.donations.map((d) => (
              <DonationRowView key={d.id} donation={d} canManage={canManage} onCancel={(reason) => cancel.mutate({ id: d.id, reason })} busy={cancel.isPending} />
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="flex-1 rounded-lg border border-line bg-surface px-5 py-4">
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
      <p className="tabular mt-1 text-3xl font-bold text-ink-900">{value}</p>
      <p className="mt-1 text-sm text-ink-600">{detail}</p>
    </div>
  );
}

function DonationRowView({ donation: d, canManage, onCancel, busy }: { donation: DonationRow; canManage: boolean; onCancel: (reason: string) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const detail = useQuery({ queryKey: ['donations', 'detail', d.id], queryFn: () => donationEndpoints.donation(d.id), enabled: open });
  return (
    <>
      <tr className="border-b border-line last:border-0">
        <td className="px-5 py-3 text-sm text-ink-600 whitespace-nowrap">{formatWhen(d.succeeded_at ?? d.created_at)}</td>
        <td className="px-5 py-3">
          <div className="font-medium text-ink-900">{d.anonymous ? 'Anonymous' : (d.donor_name ?? '—')}</div>
          <div className="text-xs text-ink-400">
            {d.donor_email ?? ''}
            {d.customer_sub ? ' · has account' : ' · guest'}
          </div>
        </td>
        <td className="px-5 py-3 text-sm text-ink-600">{d.campaign_name}</td>
        <td className="tabular px-5 py-3 text-sm text-ink-600">
          {dollars(d.amount_cents)}
          {d.kind === 'RECURRING' ? <span className="text-xs"> / {d.interval === 'YEAR' ? 'year' : 'month'}</span> : null}
          <div className="text-xs text-ink-400">
            {humanize(d.kind)}
            {d.kind === 'RECURRING' ? ` · ${d.payments_count ?? 0} payments · ${dollars(d.paid_cents ?? 0)} total` : ''}
            {d.stripe_mode === 'TEST' ? ' · test' : ''}
          </div>
        </td>
        <td className="px-5 py-3">
          <Badge tone={statusTone(d.status)}>{d.status}</Badge>
          {d.status === 'PAUSED' ? <div className="mt-1 text-xs text-ink-400">{d.paused_until ? `until ${formatWhen(d.paused_until)}` : 'until resumed'}</div> : null}
        </td>
        <td className="px-5 py-3 text-right whitespace-nowrap">
          <Button onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Details'}</Button>
          {canManage && d.kind === 'RECURRING' && (d.status === 'ACTIVE' || d.status === 'PAST_DUE' || d.status === 'PAUSED') && !open ? (
            <Button className="ml-2" onClick={() => setOpen(true)}>
              Manage
            </Button>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-line bg-page last:border-0">
          <td colSpan={6} className="px-5 py-3 text-sm text-ink-600">
            {d.message ? <p className="mb-2 italic">“{d.message}”</p> : null}
            {canManage && d.kind === 'RECURRING' && (d.status === 'ACTIVE' || d.status === 'PAST_DUE' || d.status === 'PAUSED') ? <RecurringControls donation={d} onCancel={onCancel} busy={busy} /> : null}
            <p className="text-xs text-ink-400">
              Stripe {d.stripe_payment_intent_id ? `PI ${d.stripe_payment_intent_id}` : ''} {d.stripe_subscription_id ? `subscription ${d.stripe_subscription_id}` : ''} · donation {d.id}
            </p>
            {detail.data ? (
              <ul className="mt-2 space-y-0.5 text-xs">
                {detail.data.payments.map((p) => (
                  <li key={p.id} className="tabular">
                    {formatWhen(p.paid_at)} · {dollars(p.amount_cents)} · {p.status.toLowerCase()} {p.stripe_invoice_id ? `· ${p.stripe_invoice_id}` : ''}
                  </li>
                ))}
                {detail.data.payments.length === 0 ? <li>No payments recorded yet.</li> : null}
              </ul>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function CampaignForm({ campaign, onDone, onCancel, onError }: { campaign: DonationCampaignRow | null; onDone: () => void; onCancel: () => void; onError: (message: string) => void }) {
  const [form, setForm] = useState({
    slug: campaign?.slug ?? '',
    name: campaign?.name ?? '',
    tagline: campaign?.tagline ?? '',
    description_md: campaign?.description_md ?? '',
    suggested: (campaign?.suggested_amounts ?? [2500, 5000, 10000, 25000]).map((a) => (a / 100).toFixed(2)).join(', '),
    min_amount: ((campaign?.min_amount_cents ?? 500) / 100).toFixed(2),
    allow_custom_amount: campaign?.allow_custom_amount ?? true,
    allow_recurring: campaign?.allow_recurring ?? true,
    offer_in_checkout: campaign?.offer_in_checkout ?? false,
    default_for_refunds: campaign?.default_for_refunds ?? false,
    checkout_prompt: campaign?.checkout_prompt ?? '',
    checkout_note: campaign?.checkout_note ?? '',
    tax_deductible: campaign?.tax_deductible ?? false,
    receipt_note: campaign?.receipt_note ?? '',
    goal: campaign?.goal_cents ? (campaign.goal_cents / 100).toFixed(2) : '',
    show_progress: campaign?.show_progress ?? false,
    progress_offset: campaign?.progress_offset_cents ? (campaign.progress_offset_cents / 100).toFixed(2) : '',
    active: campaign?.active ?? true,
    sort_order: String(campaign?.sort_order ?? 100),
    show_donor_wall: campaign?.show_donor_wall ?? true,
    wall_review_required: campaign?.wall_review_required ?? false,
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));
  const save = useMutation({
    mutationFn: () => {
      const suggested = form.suggested.split(',').map((s) => toCents(s.trim())).filter((n): n is number => n !== null && n > 0);
      const minimum = toCents(form.min_amount);
      if (suggested.length === 0) throw new Error('Give at least one suggested amount');
      if (minimum === null) throw new Error('Minimum amount is not a number');
      const input = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        tagline: form.tagline.trim() || null,
        description_md: form.description_md.trim() || null,
        show_donor_wall: form.show_donor_wall,
        wall_review_required: form.wall_review_required,
        suggested_amounts: suggested,
        min_amount_cents: minimum,
        allow_custom_amount: form.allow_custom_amount,
        allow_recurring: form.allow_recurring,
        offer_in_checkout: form.offer_in_checkout,
        default_for_refunds: form.default_for_refunds,
        checkout_prompt: form.checkout_prompt.trim() || null,
        checkout_note: form.checkout_note.trim() || null,
        tax_deductible: form.tax_deductible,
        receipt_note: form.receipt_note.trim() || null,
        goal_cents: form.goal.trim() ? toCents(form.goal) : null,
        show_progress: form.show_progress,
        progress_offset_cents: form.progress_offset.trim() ? (toCents(form.progress_offset) ?? 0) : 0,
        active: form.active,
        sort_order: Number(form.sort_order) || 100,
      };
      return (campaign ? donationEndpoints.updateCampaign(campaign.id, input) : donationEndpoints.createCampaign(input));
    },
    onSuccess: onDone,
    onError: (cause: Error) => onError(cause.message),
  });
  return (
    <Panel title={campaign ? `Edit ${campaign.name}` : 'New campaign'} description="Slug becomes the storefront address; amounts are in dollars.">
      <form
        className="grid gap-4 px-5 py-4 md:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name" htmlFor="dc-name">
          <input id="dc-name" className={`${INPUT} w-full`} value={form.name} onChange={(e) => set('name', e.target.value)} required />
        </Field>
        <Field label="Slug" htmlFor="dc-slug" hint="lowercase, dashes; /donate/<slug>">
          <input id="dc-slug" className={`${INPUT} w-full font-mono`} value={form.slug} onChange={(e) => set('slug', e.target.value)} required pattern="[a-z0-9]+(-[a-z0-9]+)*" />
        </Field>
        <Field label="Tagline" htmlFor="dc-tagline" hint="Under the name on /donate and the campaign page.">
          <input id="dc-tagline" className={`${INPUT} w-full`} value={form.tagline} onChange={(e) => set('tagline', e.target.value)} />
        </Field>
        <Field label="Sort order" htmlFor="dc-sort">
          <input id="dc-sort" type="number" className={`${INPUT} w-32`} value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Description" htmlFor="dc-desc" hint="Shown on the campaign page.">
            <textarea id="dc-desc" className={`${INPUT} min-h-24 w-full`} value={form.description_md} onChange={(e) => set('description_md', e.target.value)} />
          </Field>
        </div>
        <Field label="Suggested amounts" htmlFor="dc-amounts" hint="Comma-separated dollars, e.g. 25, 50, 100, 250">
          <input id="dc-amounts" className={`${INPUT} w-full`} value={form.suggested} onChange={(e) => set('suggested', e.target.value)} />
        </Field>
        <Field label="Minimum gift" htmlFor="dc-min">
          <input id="dc-min" className={`${INPUT} w-32`} value={form.min_amount} onChange={(e) => set('min_amount', e.target.value)} />
        </Field>
        <div className="space-y-2 text-sm text-ink-700">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.allow_custom_amount} onChange={(e) => set('allow_custom_amount', e.target.checked)} /> Allow a custom amount
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.allow_recurring} onChange={(e) => set('allow_recurring', e.target.checked)} /> Offer monthly / yearly giving
          </label>
          {/* Donors who chose to be listed, by giving tier (D-332). Only gifts made with a listing choice appear. */}
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.show_donor_wall} onChange={(e) => set('show_donor_wall', e.target.checked)} /> Show a donor wall on the page
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.wall_review_required} disabled={!form.show_donor_wall} onChange={(e) => set('wall_review_required', e.target.checked)} /> Review each new name before it appears
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.tax_deductible} onChange={(e) => set('tax_deductible', e.target.checked)} /> Tax-deductible (say so on the receipt)
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Active (shown on the storefront)
          </label>
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={form.offer_in_checkout} onChange={(e) => set('offer_in_checkout', e.target.checked)} /> Offer in the ticket checkout
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={form.default_for_refunds} onChange={(e) => set('default_for_refunds', e.target.checked)} /> Receives refunds converted to gifts by default
          </label>
          <Field label="Goal" htmlFor="dc-goal" hint="Dollars. Leave empty for no goal.">
          <input id="dc-goal" className={INPUT} value={form.goal} onChange={(e) => set('goal', e.target.value)} inputMode="decimal" placeholder="50000" />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.show_progress} disabled={!form.goal.trim()} onChange={(e) => set('show_progress', e.target.checked)} /> Show the thermometer on the donation page
        </label>
        <Field label="Raised elsewhere" htmlFor="dc-offset" hint="Dollars taken outside Ticketing that count toward the goal (checks, pledges).">
          <input id="dc-offset" className={INPUT} value={form.progress_offset} onChange={(e) => set('progress_offset', e.target.value)} inputMode="decimal" placeholder="0" />
        </Field>
        <Field label="Checkout prompt" htmlFor="dc-prompt" hint="e.g. Add a gift to the Auditorium Restoration Fund?">
            <input id="dc-prompt" className={`${INPUT} w-full`} value={form.checkout_prompt} onChange={(e) => set('checkout_prompt', e.target.value)} disabled={!form.offer_in_checkout} />
          </Field>
          <Field label="Checkout note" htmlFor="dc-note" hint="Optional line under the prompt; blank shows nothing.">
            <input id="dc-note" className={`${INPUT} w-full`} value={form.checkout_note} onChange={(e) => set('checkout_note', e.target.value)} disabled={!form.offer_in_checkout} />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Receipt note" htmlFor="dc-receipt" hint="Appears on the thank-you page and receipt (tax id, thanks).">
            <input id="dc-receipt" className={`${INPUT} w-full`} value={form.receipt_note} onChange={(e) => set('receipt_note', e.target.value)} />
          </Field>
        </div>
        <div className="flex gap-2 md:col-span-2">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : campaign ? 'Save changes' : 'Create campaign'}
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

/**
 * A campaign's page, in the event page's editor (D-332). Its sponsor blocks can
 * use any sponsor, since a fund has no event to narrow them to. Saved when
 * published; there is no separate draft, so the page changes when you publish.
 */
function CampaignPageEditor({ campaign, onDone, onError }: { campaign: DonationCampaignRow; onDone: () => void; onError: (message: string) => void }) {
  const sponsors = useQuery({ queryKey: ['donation-sponsors'], queryFn: donationEndpoints.sponsors });
  const [preview, setPreview] = useState<string | null>(null);
  const publish = useMutation({
    mutationFn: (doc: unknown) => donationEndpoints.saveContent(campaign.id, doc),
    onSuccess: onDone,
    onError: (cause: ApiError) => onError(cause.message),
  });
  const previewIt = useMutation({
    mutationFn: (doc: unknown) => donationEndpoints.previewContent(campaign.id, doc),
    onSuccess: (result) => setPreview(result.html),
    onError: (cause: ApiError) => onError(cause.message),
  });
  return (
    <>
      <Panel
        title={`The ${campaign.name} page`}
        description="What a donor reads above the gift form. Images, sponsor logos (any sponsor, not only an event's), callouts and buttons. The description above is shown only when this is empty."
        actions={<Button onClick={onDone}>Close</Button>}
      >
        <div className="px-5 py-4">
          {sponsors.isPending ? (
            <p className="text-sm text-ink-400">Loading…</p>
          ) : (
            <ContentEditor
              content={campaign.content}
              sponsors={(sponsors.data?.sponsors ?? []).map((s) => ({ id: s.id, name: s.name, display_name: s.display_name, tier_code: s.tier_code, logos: s.logos }))}
              busy={publish.isPending}
              onAutosave={() => undefined}
              onPublish={(doc) => publish.mutate(doc)}
              onPreview={(doc) => previewIt.mutate(doc)}
            />
          )}
        </div>
      </Panel>
      {preview !== null ? (
        <Panel title="Preview" description="Rendered by the same code the storefront uses." actions={<Button onClick={() => setPreview(null)}>Close</Button>}>
          <div className="prose max-w-none px-5 py-4" dangerouslySetInnerHTML={{ __html: preview }} />
        </Panel>
      ) : null}
    </>
  );
}

const LISTING_LABEL: Record<WallListingRow['wall_listing'], string> = { NAME: 'By name', ANONYMOUS: 'Anonymous', IN_MEMORY: 'In memory' };

/**
 * The donor wall, moderated (D-338): take a name down, correct the name it
 * shows, fix memorial text, or approve a new listing when the campaign holds
 * them for review. The gift and the donor's tax statement never change here.
 */
function WallModeration({ campaign, canManage, onClose, onError }: { campaign: DonationCampaignRow; canManage: boolean; onClose: () => void; onError: (message: string) => void }) {
  const queryClient = useQueryClient();
  const wall = useQuery({ queryKey: ['donations', 'wall', campaign.id], queryFn: () => donationEndpoints.wall(campaign.id) });
  const [editing, setEditing] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [show, setShow] = useState<'ALL' | 'WAITING' | 'HIDDEN'>('ALL');
  const moderate = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof donationEndpoints.moderate>[1] }) => donationEndpoints.moderate(id, input),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['donations', 'wall', campaign.id] });
    },
    onError: (cause: ApiError) => onError(cause.message),
  });

  const reviewRequired = wall.data?.review_required ?? false;
  const all = wall.data?.listings ?? [];
  const waiting = all.filter((l) => reviewRequired && !l.wall_reviewed_at && !l.wall_hidden_at);
  const rows = show === 'WAITING' ? waiting : show === 'HIDDEN' ? all.filter((l) => l.wall_hidden_at) : all;
  const shownAs = (l: WallListingRow) => (l.wall_listing === 'ANONYMOUS' ? 'Anonymous' : l.wall_listing === 'IN_MEMORY' ? `In memory of ${l.in_memory_of ?? ''}` : l.wall_name || l.donor_name || 'A friend');

  return (
    <Panel
      title={`Donor wall · ${campaign.name}`}
      description={reviewRequired ? `New names wait here until somebody approves them. ${waiting.length} waiting.` : 'Names appear as soon as the gift goes through. Take one down or correct it here.'}
      actions={
        <div className="flex flex-wrap gap-2">
          <select className={INPUT} value={show} onChange={(e) => setShow(e.target.value as typeof show)} aria-label="Show">
            <option value="ALL">Everything ({all.length})</option>
            {reviewRequired ? <option value="WAITING">Waiting for review ({waiting.length})</option> : null}
            <option value="HIDDEN">Taken down</option>
          </select>
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      <Table headers={['On the wall as', 'Donor', 'Given', 'Status', '']}>
        {rows.length === 0 ? (
          <EmptyRow columns={5}>{wall.isLoading ? 'Loading…' : 'Nothing here.'}</EmptyRow>
        ) : (
          rows.map((l) => {
            const status = l.wall_hidden_at ? 'taken down' : reviewRequired && !l.wall_reviewed_at ? 'waiting' : 'showing';
            const editable = l.wall_listing !== 'ANONYMOUS';
            return (
              <tr key={l.donation_id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm">
                  {editing === l.donation_id ? (
                    <form
                      className="flex gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        moderate.mutate({ id: l.donation_id, input: l.wall_listing === 'IN_MEMORY' ? { in_memory_of: text.trim() } : { wall_name: text.trim() || null } });
                      }}
                    >
                      <input value={text} onChange={(e) => setText(e.target.value)} className={`w-56 ${INPUT}`} aria-label={l.wall_listing === 'IN_MEMORY' ? 'In memory of' : 'Name on the wall'} autoFocus />
                      <Button type="submit" variant="primary" disabled={moderate.isPending}>Save</Button>
                      <Button type="button" onClick={() => setEditing(null)}>Cancel</Button>
                    </form>
                  ) : (
                    <>
                      <span className={l.wall_hidden_at ? 'text-ink-400 line-through' : 'font-medium'}>{shownAs(l)}</span>
                      <div className="text-xs text-ink-400">
                        {LISTING_LABEL[l.wall_listing]}
                        {l.wall_name ? ` · corrected from "${l.donor_name ?? ''}"` : ''}
                      </div>
                    </>
                  )}
                </td>
                <td className="px-5 py-3 text-sm">
                  {l.donor_name ?? '—'}
                  <div className="text-xs text-ink-400">{l.donor_email}</div>
                </td>
                <td className="tabular px-5 py-3 text-sm">
                  {dollars(l.total_cents)}
                  <div className="text-xs text-ink-400">{formatWhen(l.last_given_at)}</div>
                </td>
                <td className="px-5 py-3 text-sm">
                  <Badge tone={status === 'showing' ? 'ok' : status === 'waiting' ? 'warn' : 'neutral'}>{status}</Badge>
                </td>
                <td className="px-5 py-3 text-right text-sm whitespace-nowrap">
                  {canManage && editing !== l.donation_id ? (
                    <span className="inline-flex gap-2">
                      {status === 'waiting' ? <Button variant="primary" disabled={moderate.isPending} onClick={() => moderate.mutate({ id: l.donation_id, input: { reviewed: true } })}>Approve</Button> : null}
                      {editable ? <Button onClick={() => { setEditing(l.donation_id); setText(l.wall_listing === 'IN_MEMORY' ? (l.in_memory_of ?? '') : (l.wall_name ?? l.donor_name ?? '')); }}>{l.wall_listing === 'IN_MEMORY' ? 'Fix text' : 'Correct name'}</Button> : null}
                      <Button variant={l.wall_hidden_at ? 'secondary' : 'danger'} disabled={moderate.isPending} onClick={() => moderate.mutate({ id: l.donation_id, input: { hidden: !l.wall_hidden_at } })}>
                        {l.wall_hidden_at ? 'Put back' : 'Take down'}
                      </Button>
                    </span>
                  ) : null}
                </td>
              </tr>
            );
          })
        )}
      </Table>
    </Panel>
  );
}

/**
 * A monthly gift, managed (D-339): skip it for a while, pick it up again,
 * change the amount from the next gift on, or end it. Replaces the browser
 * prompt that was the only thing here, and could only cancel.
 */
function RecurringControls({ donation: d, onCancel, busy }: { donation: DonationRow; onCancel: (reason: string) => void; busy: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<'pause' | 'amount' | 'cancel' | null>(null);
  const [resumesOn, setResumesOn] = useState('');
  const [amount, setAmount] = useState((d.amount_cents / 100).toFixed(2));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const done = () => {
    setOpen(null);
    setReason('');
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['donations'] });
  };
  const fail = (cause: ApiError) => setError(cause.message);
  const pause = useMutation({ mutationFn: () => donationEndpoints.pauseRecurring(d.id, { resumes_on: resumesOn || null, reason: reason.trim() || null }), onSuccess: done, onError: fail });
  const resume = useMutation({ mutationFn: () => donationEndpoints.resumeRecurring(d.id), onSuccess: done, onError: fail });
  const change = useMutation({
    mutationFn: () => {
      const cents = toCents(amount);
      if (cents === null || cents <= 0) throw new Error('Enter an amount in dollars');
      return donationEndpoints.changeRecurringAmount(d.id, { amount_cents: cents, reason: reason.trim() || null });
    },
    onSuccess: done,
    onError: (cause: Error) => setError(cause.message),
  });
  const per = d.interval === 'YEAR' ? 'year' : 'month';

  return (
    <div className="mb-3 space-y-2 rounded border border-line bg-surface p-3">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="flex flex-wrap gap-2">
        {d.status === 'PAUSED' ? (
          <Button variant="primary" disabled={resume.isPending} onClick={() => resume.mutate()}>
            {resume.isPending ? 'Resuming…' : 'Resume now'}
          </Button>
        ) : (
          <Button onClick={() => setOpen(open === 'pause' ? null : 'pause')}>Pause</Button>
        )}
        <Button onClick={() => setOpen(open === 'amount' ? null : 'amount')}>Change amount</Button>
        <Button variant="danger" onClick={() => setOpen(open === 'cancel' ? null : 'cancel')}>
          Cancel recurring
        </Button>
      </div>
      {open === 'pause' ? (
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); pause.mutate(); }}>
          <Field label="Pick up again on" htmlFor={`pause-${d.id}`}>
            <input id={`pause-${d.id}`} type="date" value={resumesOn} onChange={(e) => setResumesOn(e.target.value)} className={INPUT} />
          </Field>
          <Field label="Why" htmlFor={`pause-why-${d.id}`}>
            <input id={`pause-why-${d.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Donor is traveling" className={`w-56 ${INPUT}`} />
          </Field>
          <Button type="submit" variant="primary" disabled={pause.isPending}>
            {pause.isPending ? 'Pausing…' : resumesOn ? 'Pause until then' : 'Pause until resumed'}
          </Button>
          <p className="w-full text-xs text-ink-400">Nothing is charged while it is paused. Leave the date empty to pause until somebody resumes it.</p>
        </form>
      ) : null}
      {open === 'amount' ? (
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); change.mutate(); }}>
          <Field label={`New amount per ${per}`} htmlFor={`amount-${d.id}`}>
            <input id={`amount-${d.id}`} value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className={`tabular w-28 ${INPUT}`} />
          </Field>
          <Field label="Why" htmlFor={`amount-why-${d.id}`}>
            <input id={`amount-why-${d.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Donor phoned in" className={`w-56 ${INPUT}`} />
          </Field>
          <Button type="submit" variant="primary" disabled={change.isPending}>
            {change.isPending ? 'Saving…' : 'Change from the next gift'}
          </Button>
          <p className="w-full text-xs text-ink-400">Gifts already made keep their amounts; nobody is charged a difference.</p>
        </form>
      ) : null}
      {open === 'cancel' ? (
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); onCancel(reason.trim()); }}>
          <Field label="Why (kept in the audit log)" htmlFor={`cancel-why-${d.id}`}>
            <input id={`cancel-why-${d.id}`} value={reason} onChange={(e) => setReason(e.target.value)} required className={`w-72 ${INPUT}`} />
          </Field>
          <Button type="submit" variant="danger" disabled={busy || !reason.trim()}>
            {busy ? 'Canceling…' : 'Cancel the recurring gift'}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
